import { useState, type ComponentProps, type ReactNode } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, call } from '../../lib/api'
import { businessQuery, devicesQuery, keysQuery, meQuery, teamQuery } from '../../lib/queries'
import { ago, num } from '../../lib/format'
import { FormError } from '../../components/auth'
import { PhotoInput } from '../../components/customer-form'
import { Icon, PageHeader, Skeleton, cx, toast } from '../../components/ui'
import { JOB_TITLES } from '../../lib/options'

const TABS = [['profile', 'Profile'], ['business', 'Business'], ['billing', 'Billing'], ['team', 'Team'], ['keys', 'API Keys'], ['devices', 'Meters']] as const
type Tab = (typeof TABS)[number][0]

export const Route = createFileRoute('/_app/settings')({
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } => ({ tab: TABS.some(([t]) => t === s.tab) ? (s.tab as Tab) : undefined }),
  component: Settings,
})

// Styling from the "Onboarding details" design: peach panel, square outlined fields, dark save button.
function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-10 bg-peach-panel px-5 py-8 sm:px-12 sm:py-10">
      <h2 className="text-base font-semibold text-black">{title}</h2>
      <div className="mt-10 max-w-[722px]">{children}</div>
    </section>
  )
}

function PField({ label, action, ...p }: ComponentProps<'input'> & { label: string; action?: ReactNode }) {
  return (
    <div className="mb-6">
      <label className="mb-2 block text-xs tracking-[0.6px]" htmlFor={p.name}>{label}</label>
      <div className="flex flex-wrap items-center gap-4 sm:flex-nowrap sm:gap-12">
        <input id={p.name} {...p} className={cx('h-12 w-full min-w-0 border border-black/20 bg-transparent px-2 text-sm font-bold tracking-[0.6px] placeholder:text-black/60 focus:border-black/60 focus:outline-none read-only:text-black/60', p.className)} />
        {action}
      </div>
    </div>
  )
}

const SaveButton = ({ busy, children = 'Save Changes' }: { busy?: boolean; children?: ReactNode }) => (
  <button disabled={busy} className="mt-4 h-[43px] min-w-[163px] bg-ink-3 px-6 text-[13px] font-bold tracking-[0.6px] text-white disabled:opacity-60">{busy ? 'Saving…' : children}</button>
)
const GrayButton = (p: ComponentProps<'button'>) => (
  <button type="button" {...p} className="h-[43px] w-[163px] flex-none bg-[#e8e3e1] text-[13px] font-bold tracking-[0.6px] text-ink-3 hover:bg-white disabled:opacity-60" />
)

function useSave<T>(fn: (v: T) => Promise<unknown>, keys: string[][], msg = 'Saved') {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => (keys.forEach((queryKey) => qc.invalidateQueries({ queryKey })), toast(msg)) })
}

function Settings() {
  const tab = Route.useSearch().tab ?? 'profile'
  const { data: me } = useQuery(meQuery)
  const isAdmin = me?.user.role === 'owner' || me?.user.role === 'admin'
  return (
    <div>
      <PageHeader
        title={me?.business?.onboarded ? 'Settings' : 'Onboarding'}
        subtitle={me && !me.business?.onboarded
          ? <>Welcome! Set your prices under Billing, then <Link to="/outlets" className="font-semibold text-brand">add your first outlet</Link> and its tanks. Customers and meters come next.</>
          : 'Your profile, business, pricing, team and integrations.'}
      />
      <nav className="flex gap-8 overflow-x-auto border-b border-black/20 sm:gap-16" aria-label="Settings sections">
        {TABS.filter(([t]) => isAdmin || (t !== 'keys' && t !== 'billing')).map(([t, l]) => (
          <Link key={t} to="/settings" search={{ tab: t === 'profile' ? undefined : t }} replace className={cx('-mb-px border-b-2 py-3 text-sm whitespace-nowrap', tab === t ? 'border-ink font-semibold text-black' : 'border-transparent text-ink-3 hover:text-black')}>
            {l}
          </Link>
        ))}
      </nav>
      {tab === 'profile' && <Profile />}
      {tab === 'business' && <Business canEdit={isAdmin} />}
      {tab === 'billing' && <Billing />}
      {tab === 'team' && <Team canEdit={isAdmin} />}
      {tab === 'keys' && <Keys />}
      {tab === 'devices' && <Devices canEdit={isAdmin} />}
    </div>
  )
}

function Profile() {
  const { data } = useQuery(meQuery)
  const [avatar, setAvatar] = useState<string | null | undefined>(undefined)
  const save = useSave((f: FormData) => call(api.me.$patch({ json: {
    firstName: String(f.get('firstName')), lastName: String(f.get('lastName')), phone: String(f.get('phone') || '') || null,
    jobTitle: String(f.get('jobTitle') || '') || null, avatarKey: avatar === undefined ? data!.user.avatarKey : avatar,
  } })), [['me']], 'Profile saved')
  if (!data) return <Skeleton className="mt-10 h-96" />
  const u = data.user
  return (
    <Panel title="Your Profile">
      <form onSubmit={(e) => (e.preventDefault(), save.mutate(new FormData(e.currentTarget)))}>
        <FormError error={save.error?.message} />
        <div className="grid gap-x-10 sm:grid-cols-2">
          <PField label="First name" name="firstName" defaultValue={u.firstName} required />
          <PField label="Last name" name="lastName" defaultValue={u.lastName} required />
          <PField label="Email address" name="email" defaultValue={u.email} readOnly />
          <PField label="Phone number" name="phone" type="tel" defaultValue={u.phone ?? ''} />
          <div className="mb-6">
            <label className="mb-2 block text-xs tracking-[0.6px]" htmlFor="jobTitle">Role in the organisation</label>
            <select id="jobTitle" name="jobTitle" defaultValue={u.jobTitle ?? ''} className="h-12 w-full border border-black/20 bg-transparent px-2 text-sm font-bold">
              <option value="">Not set</option>
              {[...new Set([...JOB_TITLES, ...(u.jobTitle ? [u.jobTitle] : [])])].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <PhotoInput label="Profile photo" value={avatar === undefined ? u.avatarKey : avatar} onChange={setAvatar} />
        <SaveButton busy={save.isPending} />
      </form>
      <p className="mt-8 text-xs text-black/60">To change your password, sign out and use “Forget password?” on the sign-in page.</p>
    </Panel>
  )
}

function Business({ canEdit }: { canEdit: boolean }) {
  const { data } = useQuery(businessQuery)
  const [logo, setLogo] = useState<string | null | undefined>(undefined)
  const save = useSave((f: FormData) => call(api.business.$patch({ json: {
    name: String(f.get('name')), address: String(f.get('address') || '') || null, rcNumber: String(f.get('rcNumber') || '') || null,
    logoKey: logo === undefined ? data!.business.logoKey : logo, addDepotKg: Number(f.get('addDepotKg') || 0) || undefined,
  } })), [['settings', 'business'], ['me'], ['outlet']], 'Business saved')
  if (!data) return <Skeleton className="mt-10 h-96" />
  const b = data.business
  return (
    <Panel title="Business Details">
      <form onSubmit={(e) => { e.preventDefault(); const form = e.currentTarget; save.mutate(new FormData(form), { onSuccess: () => ((form.elements.namedItem('addDepotKg') as HTMLInputElement).value = '') }) }}>
        <FormError error={save.error?.message} />
        <fieldset disabled={!canEdit}>
          <div className="grid gap-x-10 sm:grid-cols-2">
            <PField label="Business name" name="name" defaultValue={b.name} required />
            <PField label="RC number" name="rcNumber" defaultValue={b.rcNumber ?? ''} />
          </div>
          <PField label="Business address" name="address" defaultValue={b.address ?? ''} />
          <PField label={`Depot stock — ${num(b.depotBalanceKg)}kg available to supply outlets`} name="addDepotKg" type="number" step="1" placeholder="Add kg received at the depot (negative to correct)" />
          <PhotoInput label="Logo" value={logo === undefined ? b.logoKey : logo} onChange={setLogo} />
          {canEdit && <SaveButton busy={save.isPending} />}
        </fieldset>
      </form>
    </Panel>
  )
}

function Billing() {
  const { data } = useQuery(businessQuery)
  const save = useSave((f: FormData) => call(api.business.$patch({ json: {
    pricePerKg: Number(f.get('pricePerKg')), wholesalePerKg: Number(f.get('wholesalePerKg')), billingEmail: String(f.get('billingEmail') || '') || null,
  } })), [['settings', 'business'], ['customer'], ['outlet']], 'Prices saved')
  if (!data) return <Skeleton className="mt-10 h-96" />
  const b = data.business
  return (
    <Panel title="Pricing & Billing">
      <form onSubmit={(e) => (e.preventDefault(), save.mutate(new FormData(e.currentTarget)))}>
        <FormError error={save.error?.message} />
        <div className="grid gap-x-10 sm:grid-cols-2">
          <PField label="Retail price per kg (₦) — customer refills" name="pricePerKg" type="number" step="0.01" min="0" defaultValue={b.pricePerKg} required />
          <PField label="Wholesale price per kg (₦) — outlet deliveries" name="wholesalePerKg" type="number" step="0.01" min="0" defaultValue={b.wholesalePerKg} required />
        </div>
        <PField label="Billing email" name="billingEmail" type="email" defaultValue={b.billingEmail ?? ''} />
        <p className="text-xs text-black/60">New prices apply to orders created from now on; existing orders keep the price they were raised at.</p>
        <SaveButton busy={save.isPending} />
      </form>
    </Panel>
  )
}

function Team({ canEdit }: { canEdit: boolean }) {
  const { data } = useQuery(teamQuery)
  const qc = useQueryClient()
  const invite = useSave((f: FormData) => call(api.team.$post({ json: {
    firstName: String(f.get('firstName')), lastName: String(f.get('lastName')), email: String(f.get('email')), role: f.get('role') as 'admin' | 'member',
  } })), [['settings', 'team']], 'Invite sent')
  const remove = useMutation({
    mutationFn: (id: string) => call(api.team[':id'].$delete({ param: { id } })),
    onSuccess: () => (qc.invalidateQueries({ queryKey: ['settings', 'team'] }), toast('Removed from team')),
    onError: (e) => toast(e.message, 'error'),
  })
  return (
    <Panel title="Team">
      <ul className="divide-y divide-black/10 border-y border-black/10">
        {data?.members.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-x-6 gap-y-1 py-3 text-sm">
            <span className="min-w-0 flex-1"><b>{m.firstName} {m.lastName}</b> <span className="text-black/60">{m.email}</span></span>
            <span className="text-xs capitalize">{m.role}{m.invited && ' · invite pending'}</span>
            {canEdit && m.role !== 'owner' && <button onClick={() => confirm(`Remove ${m.firstName}?`) && remove.mutate(m.id)} className="text-xs font-semibold text-danger">Remove</button>}
          </li>
        ))}
      </ul>
      {canEdit && (
        <form className="mt-10" onSubmit={(e) => { e.preventDefault(); const form = e.currentTarget; invite.mutate(new FormData(form), { onSuccess: () => form.reset() }) }}>
          <h3 className="mb-4 text-sm font-semibold">Invite a teammate</h3>
          <FormError error={invite.error?.message} />
          <div className="grid gap-x-10 sm:grid-cols-2">
            <PField label="First name" name="firstName" required />
            <PField label="Last name" name="lastName" required />
            <PField label="Email address" name="email" type="email" required />
            <div className="mb-6">
              <label className="mb-2 block text-xs tracking-[0.6px]" htmlFor="role">Access</label>
              <select id="role" name="role" className="h-12 w-full border border-black/20 bg-transparent px-2 text-sm font-bold">
                <option value="member">Member — day-to-day operations</option>
                <option value="admin">Admin — also team, prices and API keys</option>
              </select>
            </div>
          </div>
          <SaveButton busy={invite.isPending}>Send invite</SaveButton>
        </form>
      )}
    </Panel>
  )
}

function Secret({ value, placeholder }: { value: string | null; placeholder: string }) {
  const [show, setShow] = useState(false)
  return (
    <div className="flex h-12 w-full min-w-0 items-center border border-black/20 px-2">
      <input readOnly value={value ? (show ? value : value.slice(0, 12) + '•'.repeat(12)) : ''} placeholder={placeholder} className="min-w-0 flex-1 bg-transparent font-mono text-sm font-bold placeholder:font-sans placeholder:text-black/60 focus:outline-none" onFocus={(e) => show && e.target.select()} />
      {value && (
        <>
          <button type="button" onClick={() => setShow(!show)} aria-label={show ? 'Hide' : 'Reveal'} className="p-1"><Icon name="eye" className="size-5" /></button>
          <button type="button" onClick={() => (navigator.clipboard.writeText(value), toast('Copied'))} className="px-1 text-xs font-semibold">Copy</button>
        </>
      )}
    </div>
  )
}

function Keys() {
  const { data } = useQuery(keysQuery)
  const qc = useQueryClient()
  const [fresh, setFresh] = useState<string | null>(null)
  const rotateSecret = useMutation({
    mutationFn: () => call(api.keys.secret.$post()),
    onSuccess: (r) => (setFresh(r.secretKey), qc.invalidateQueries({ queryKey: ['settings', 'keys'] }), toast('New secret key created — copy it now')),
  })
  const rotatePublic = useMutation({ mutationFn: () => call(api.keys.public.$post()), onSuccess: () => (qc.invalidateQueries({ queryKey: ['settings', 'keys'] }), toast('New public key created')) })
  const webhook = useSave((url: string) => call(api.keys.webhook.$patch({ json: { webhookUrl: url } })), [['settings', 'keys']], 'Webhook saved')
  if (!data) return <Skeleton className="mt-10 h-96" />
  const confirmRotate = (what: string, go: () => void) => (!data.secretKeyPrefix && what === 'secret') || confirm(`Create a new ${what} key? The current one stops working immediately.`) ? go() : undefined

  return (
    <Panel title="API Configuration">
      <FormError error={webhook.error?.message ?? rotateSecret.error?.message} />
      <div className="mb-6">
        <label className="mb-2 block text-xs tracking-[0.6px]">Live Secret Key</label>
        <div className="flex flex-wrap items-center gap-4 sm:flex-nowrap sm:gap-12">
          <Secret value={fresh} placeholder={data.secretKeyPrefix ? `${data.secretKeyPrefix}… (hidden — create a new one to see it)` : 'Live secret key'} />
          <GrayButton disabled={rotateSecret.isPending} onClick={() => confirmRotate('secret', () => rotateSecret.mutate())}>Create New</GrayButton>
        </div>
        {fresh && <p className="mt-2 text-xs font-semibold">Copy this key now — for your security it won't be shown again.</p>}
      </div>
      <div className="mb-6">
        <label className="mb-2 block text-xs tracking-[0.6px]">Live Public Key</label>
        <div className="flex flex-wrap items-center gap-4 sm:flex-nowrap sm:gap-12">
          <Secret value={data.publicKey} placeholder="Live public key" />
          <GrayButton disabled={rotatePublic.isPending} onClick={() => confirmRotate('public', () => rotatePublic.mutate())}>Create New</GrayButton>
        </div>
      </div>
      <form onSubmit={(e) => (e.preventDefault(), webhook.mutate(String(new FormData(e.currentTarget).get('webhookUrl') ?? '')))}>
        <PField label="Webhook URL" name="webhookUrl" type="url" defaultValue={data.webhookUrl ?? ''} placeholder="Live webhook URL" className="sm:max-w-[722px]" />
        <SaveButton busy={webhook.isPending} />
      </form>
      <div className="mt-10 space-y-3 border-t border-black/10 pt-6 text-xs leading-5 text-black/70">
        <p><b className="text-black">Meter readings in:</b> point your LoRaWAN network server's HTTP integration at <code className="rounded bg-white/60 px-1 font-mono">POST {data.ingestUrl}</code> with header <code className="rounded bg-white/60 px-1 font-mono">Authorization: Bearer sk_live_…</code> (or <code className="font-mono">?key=sk_live_…</code>). Everynet, ChirpStack v4 (JSON), The Things Stack v3 and ThingPark uplinks work unchanged.</p>
        <p><b className="text-black">Events out:</b> we POST <code className="font-mono">reading</code>, <code className="font-mono">customer.low_gas</code> and <code className="font-mono">order.*</code> events to your webhook, signed with HMAC-SHA256 in <code className="font-mono">X-GasRail-Signature</code> using <code className="rounded bg-white/60 px-1 font-mono break-all">{data.webhookSecret}</code>.</p>
        <p><b className="text-black">Public key:</b> read-only gas levels for your own apps at <code className="font-mono">GET /api/v1/levels?key=pk_live_…</code> (no customer details).</p>
      </div>
    </Panel>
  )
}

function Devices({ canEdit }: { canEdit: boolean }) {
  const { data } = useQuery(devicesQuery)
  const qc = useQueryClient()
  const remove = useMutation({
    mutationFn: (id: string) => call(api.devices[':id'].$delete({ param: { id } })),
    onSuccess: () => (qc.invalidateQueries({ queryKey: ['settings', 'devices'] }), toast('Meter removed')),
  })
  const online = (t: number | null) => t != null && Date.now() - t < 86_400_000
  return (
    <Panel title="Meters">
      <p className="-mt-6 mb-6 text-xs text-black/60">Every gas meter that has been linked or has sent data. Link a meter by entering its DevEUI on a customer or tank.</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="text-xs text-black/60"><tr><th className="py-2 font-normal">DevEUI</th><th className="font-normal">Attached to</th><th className="font-normal">Last reading</th><th className="font-normal">Signal</th><th /></tr></thead>
          <tbody className="divide-y divide-black/10">
            {data?.devices.map((d) => (
              <tr key={d.id}>
                <td className="py-3 font-mono text-xs">{d.devEui}</td>
                <td>{d.attachedTo ? (d.attachedTo.kind === 'customer' ? <Link to="/customers/$customerId" params={{ customerId: d.attachedTo.id }} className="font-semibold hover:underline">{d.attachedTo.name}</Link> : d.attachedTo.name) : <span className="font-semibold text-brand-dark">Unassigned</span>}</td>
                <td><span className={cx('mr-1.5 inline-block size-2 rounded-full', online(d.lastSeenAt) ? 'bg-good-2' : 'bg-black/20')} />{d.lastSeenAt ? ago(d.lastSeenAt) : 'never'}</td>
                <td className="text-xs">{d.rssi != null ? `${d.rssi} dBm · SNR ${d.snr}` : '—'}</td>
                <td className="text-right">{canEdit && <button onClick={() => confirm(`Remove meter ${d.devEui}?`) && remove.mutate(d.id)} className="text-xs font-semibold text-danger">Remove</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !data.devices.length && <p className="py-6 text-sm text-black/60">No meters yet.</p>}
      </div>
    </Panel>
  )
}
